import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ChevronLeft, Trash2, Plus, Sparkles, Loader2, Share2, Send, UserPlus, Copy, MessageSquare, X, CalendarDays } from "lucide-react";
import ShareDialog from "@/components/ShareDialog";
import InvitePortalShare from "@/components/InvitePortalShare";
import { useConfirm } from "@/components/ConfirmDialog";

export default function ClientDetailPage() {
    const { id } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const [client, setClient] = useState(null);
    const [workouts, setWorkouts] = useState([]);
    const [notes, setNotes] = useState([]);
    const [payments, setPayments] = useState([]);
    const [shareTarget, setShareTarget] = useState(null);
    const [habits, setHabits] = useState([]);
    const [checkIns, setCheckIns] = useState([]);
    const [progressPhotos, setProgressPhotos] = useState([]);
    const [photoLightbox, setPhotoLightbox] = useState(null);
    const [intake, setIntake] = useState(null);
    const [newHabit, setNewHabit] = useState({ name: "", frequency: "daily", target: "" });
    const [inviteResult, setInviteResult] = useState(null);
    const [inviting, setInviting] = useState(false);
    const [noteBody, setNoteBody] = useState("");
    const [noteTitle, setNoteTitle] = useState("");
    const [aiBusy, setAiBusy] = useState(false);
    const [aiResult, setAiResult] = useState(null);
    const [aiErr, setAiErr] = useState("");
    const [genOpen, setGenOpen] = useState(false);
    const confirm = useConfirm();

    const load = async () => {
        const [c, w, n, p, h, ci, pp, intk] = await Promise.all([
            api.get(`/clients/${id}`),
            api.get(`/workouts`, { params: { client_id: id } }),
            api.get(`/notes`, { params: { client_id: id } }),
            api.get(`/payments`),
            api.get(`/habits`, { params: { client_id: id } }),
            api.get(`/check-ins`, { params: { client_id: id } }),
            api.get(`/progress-photos`, { params: { client_id: id } }),
            api.get(`/clients/${id}/intake`).catch(() => ({ data: null })),
        ]);
        setClient(c.data);
        setWorkouts(w.data);
        setNotes(n.data);
        setPayments(p.data.filter((x) => x.client_id === id));
        setHabits(h.data);
        setCheckIns(ci.data);
        setProgressPhotos(pp.data);
        setIntake(intk.data && Object.keys(intk.data).length ? intk.data : null);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const invitePortal = async () => {
        setInviting(true); setInviteResult(null);
        try {
            const { data } = await api.post(`/clients/${id}/invite`);
            setInviteResult(data);
        } catch (e) {
            setInviteResult({ ok: false, error: formatApiError(e.response?.data?.detail) || e.message });
        } finally { setInviting(false); }
    };

    const addHabit = async (e) => {
        e.preventDefault();
        if (!newHabit.name.trim()) return;
        const { data } = await api.post("/habits", { ...newHabit, client_id: id });
        setHabits([data, ...habits]);
        setNewHabit({ name: "", frequency: "daily", target: "" });
    };

    const removeHabit = async (hid) => {
        await api.delete(`/habits/${hid}`);
        setHabits(habits.filter((h) => h.id !== hid));
    };

    const generateSummary = async () => {
        setAiBusy(true); setAiErr(""); setAiResult(null);
        try {
            const { data } = await api.get(`/ai/client-summary/${id}`);
            setAiResult(data);
        } catch (e) {
            setAiErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setAiBusy(false); }
    };

    const addNote = async (e) => {
        e.preventDefault();
        if (!noteBody.trim()) return;
        const { data } = await api.post("/notes", { client_id: id, title: noteTitle, body: noteBody });
        setNotes([data, ...notes]);
        setNoteBody(""); setNoteTitle("");
    };

    const removeNote = async (nid) => {
        await api.delete(`/notes/${nid}`);
        setNotes(notes.filter((n) => n.id !== nid));
    };

    const removeClient = async () => {
        const ok = await confirm({ title: "Delete this client?", message: `${client.name} will be removed along with all workouts, notes, and progress data.`, confirmLabel: "Delete client" });
        if (!ok) return;
        await api.delete(`/clients/${id}`);
        nav("/clients");
    };

    if (!client) return <div className="p-8 text-[var(--text-muted)]">Loading…</div>;

    return (
        <div className="p-8 space-y-6" data-testid="client-detail-page">
            <Link to="/clients" className="inline-flex items-center gap-1 text-sm text-[var(--text-muted)] hover:text-white">
                <ChevronLeft size={14} /> Back to clients
            </Link>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Profile */}
                <div className="lg:col-span-1 border border-[var(--border)] bg-[var(--surface)] p-6">
                    <div className="flex items-center gap-4 mb-5">
                        {client.avatar_url ? (
                            <img src={client.avatar_url} alt="" className="w-16 h-16 object-cover" />
                        ) : (
                            <div className="w-16 h-16 bg-[var(--surface-hover)] flex items-center justify-center font-display text-2xl">
                                {client.name.charAt(0)}
                            </div>
                        )}
                        <div>
                            <h2 className="font-serif text-3xl leading-none">{client.name}</h2>
                            <span className="tag-pill mt-2 inline-block">{client.tag}</span>
                        </div>
                    </div>
                    <div className="space-y-3 text-sm">
                        <div>
                            <p className="label-tiny">Goal</p>
                            <p>{client.goal || "—"}</p>
                        </div>
                        <div>
                            <p className="label-tiny">Email</p>
                            <p className="font-mono text-xs">{client.email || "—"}</p>
                        </div>
                        <div>
                            <p className="label-tiny">Phone</p>
                            <p className="font-mono text-xs">{client.phone || "—"}</p>
                        </div>
                        <div>
                            <p className="label-tiny">Status</p>
                            <p>{client.status}</p>
                        </div>
                    </div>
                    <div className="divider my-5" />
                    {/* Message shortcut */}
                    <Link to={`/messages?client=${id}`} className="btn-ghost w-full inline-flex items-center justify-center gap-2" data-testid="message-client-btn">
                        <MessageSquare size={14} /> Message {client.name.split(" ")[0]}
                    </Link>
                    <div className="divider my-5" />
                    {/* Portal invite */}
                    <div data-testid="portal-invite-card">
                        <p className="label-tiny mb-2">/ Athlete portal</p>
                        {client.user_id ? (
                            <p className="text-xs text-[var(--gold-bright)] font-mono">✓ Portal active</p>
                        ) : (
                            <>
                                <button onClick={invitePortal} disabled={inviting} className="btn-ghost w-full inline-flex items-center gap-2 justify-center" data-testid="invite-portal-btn">
                                    <UserPlus size={14} /> {inviting ? "Generating link…" : "Invite to portal"}
                                </button>
                                {inviteResult?.ok && (
                                    <InvitePortalShare
                                        client={client}
                                        link={inviteResult.invite_link}
                                        emailSent={inviteResult.email_sent}
                                    />
                                )}
                                {inviteResult?.error && <p className="text-xs text-[var(--danger)] mt-2">{inviteResult.error}</p>}
                            </>
                        )}
                    </div>
                    <div className="divider my-5" />
                    <button onClick={removeClient} data-testid="delete-client-btn" className="text-sm text-[var(--danger)] hover:opacity-80 inline-flex items-center gap-1">
                        <Trash2 size={14} /> Delete client
                    </button>
                </div>

                {/* Workouts + Notes */}
                <div className="lg:col-span-2 space-y-6">
                    {/* AI Summary */}
                    <div className="border border-[var(--gold-deep)] bg-[var(--surface)] relative overflow-hidden" data-testid="ai-summary-card">
                        <div className="absolute inset-0 bg-stripes opacity-30 pointer-events-none" />
                        <div className="relative p-5 border-b border-[var(--border)] flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Sparkles size={18} className="text-[var(--gold-bright)]" />
                                <div>
                                    <p className="label-tiny mb-0.5 text-[var(--gold-bright)]">/ AI Insight · Claude Sonnet 4.5</p>
                                    <h3 className="font-serif text-2xl">Coach intelligence</h3>
                                </div>
                            </div>
                            <button onClick={generateSummary} disabled={aiBusy} className="btn-primary inline-flex items-center gap-2 text-xs" data-testid="generate-ai-summary">
                                {aiBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                                {aiBusy ? "Analyzing…" : aiResult ? "Regenerate" : "Generate"}
                            </button>
                        </div>
                        <div className="relative p-5">
                            {!aiResult && !aiBusy && !aiErr && (
                                <p className="text-sm text-[var(--text-muted)]">
                                    Get a sharp progress snapshot, wins, watch-outs, and the next move for {client.name} — generated from their workouts &amp; notes.
                                </p>
                            )}
                            {aiBusy && (
                                <div className="flex items-center gap-3 text-[var(--text-muted)] text-sm">
                                    <Loader2 size={14} className="animate-spin text-[var(--gold)]" />
                                    Reading {workouts.length} workouts and {notes.length} notes…
                                </div>
                            )}
                            {aiErr && <div className="text-sm text-[var(--danger)]" data-testid="ai-summary-error">{aiErr}</div>}
                            {aiResult && (
                                <div data-testid="ai-summary-result">
                                    <div className="flex items-center gap-3 mb-3">
                                        <span className="font-mono text-xs text-[var(--text-muted)]">30-day compliance</span>
                                        <span className="font-display text-2xl text-[var(--gold-bright)]">{aiResult.compliance_30d}%</span>
                                    </div>
                                    <div className="text-sm whitespace-pre-wrap leading-relaxed text-[var(--text)]"
                                         dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(
                                             (aiResult.summary || "").replace(/\*\*(.+?)\*\*/g, '<strong class="text-[var(--gold-bright)] font-serif tracking-wide">$1</strong>'),
                                             { ALLOWED_TAGS: ["strong", "em", "br"], ALLOWED_ATTR: ["class"] }
                                         ) }} />
                                    <p className="text-[10px] text-[var(--text-faint)] font-mono mt-3">Generated {new Date(aiResult.generated_at).toLocaleString()}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="border border-[var(--border)] bg-[var(--surface)]">
                        <div className="p-5 border-b border-[var(--border)] flex items-center justify-between">
                            <div>
                                <p className="label-tiny mb-1">/ Assigned</p>
                                <h3 className="font-serif text-2xl">Workouts</h3>
                            </div>
                            <div className="flex items-center gap-3">
                                <button onClick={() => setGenOpen(true)} className="text-sm text-[var(--gold)] hover:text-[var(--gold-bright)] inline-flex items-center gap-1" data-testid="open-ai-workout-gen">
                                    <Sparkles size={13} /> Generate AI plan
                                </button>
                                <Link to={`/calendar?client=${id}`} className="text-sm text-[var(--gold)] hover:text-[var(--gold-bright)]">Open in calendar →</Link>
                            </div>
                        </div>
                        {workouts.length === 0 ? (
                            <p className="p-6 text-sm text-[var(--text-muted)]">No workouts yet. Assign from the calendar.</p>
                        ) : (
                            <div>
                                {workouts.slice(0, 8).map((w) => (
                                    <div key={w.id} className="px-5 py-3 border-b border-[var(--border)] flex items-center justify-between">
                                        <div>
                                            <p className="font-medium">{w.title}</p>
                                            <p className="text-xs text-[var(--text-muted)] font-mono">{w.date} · {w.time || "—"} · {w.duration_min}m · {w.type}</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button onClick={() => setShareTarget({ kind: "workout", title: w.title, details: `${w.date} · ${w.duration_min}m` })} className="text-[var(--text-muted)] hover:text-[var(--gold)]" data-testid={`share-workout-${w.id}`}>
                                                <Share2 size={13} />
                                            </button>
                                            {w.completed ? <span className="tag-pill border-[var(--gold-bright)] text-[var(--gold-bright)]">Done</span> : <span className="tag-pill">Planned</span>}
                                            <button
                                                onClick={async () => {
                                                    const ok = await confirm({ title: "Delete workout?", message: `"${w.title}" on ${w.date} will be removed.`, confirmLabel: "Delete workout" });
                                                    if (!ok) return;
                                                    await api.delete(`/workouts/${w.id}`);
                                                    setWorkouts(workouts.filter((x) => x.id !== w.id));
                                                }}
                                                className="text-[var(--text-muted)] hover:text-[var(--danger)]"
                                                title="Delete workout"
                                                data-testid={`workout-delete-${w.id}`}
                                            >
                                                <Trash2 size={13} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="border border-[var(--border)] bg-[var(--surface)]">
                        <div className="p-5 border-b border-[var(--border)]">
                            <p className="label-tiny mb-1">/ Coaching</p>
                            <h3 className="font-serif text-2xl">Session Notes</h3>
                        </div>
                        <form onSubmit={addNote} className="p-5 border-b border-[var(--border)] space-y-3" data-testid="add-note-form">
                            <input className="input-dark" placeholder="TITLE (OPTIONAL)" value={noteTitle} onChange={(e) => setNoteTitle(e.target.value)} />
                            <textarea
                                className="input-dark min-h-[90px]"
                                placeholder="What happened in today's session?"
                                value={noteBody}
                                onChange={(e) => setNoteBody(e.target.value)}
                                data-testid="note-body-input"
                            />
                            <button type="submit" className="btn-primary inline-flex items-center gap-2" data-testid="save-note-btn">
                                <Plus size={14} /> Add note
                            </button>
                        </form>
                        <div>
                            {notes.length === 0 ? (
                                <p className="p-6 text-sm text-[var(--text-muted)]">No notes yet.</p>
                            ) : notes.map((n) => (
                                <div key={n.id} className="px-5 py-4 border-b border-[var(--border)]">
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="font-medium">{n.title || "Note"}</p>
                                        <button onClick={() => removeNote(n.id)} className="text-[var(--text-muted)] hover:text-[var(--danger)]"><Trash2 size={13} /></button>
                                    </div>
                                    <p className="text-sm text-[var(--text-muted)] whitespace-pre-wrap">{n.body}</p>
                                    <p className="text-xs text-[var(--text-faint)] mt-2 font-mono">{new Date(n.created_at).toLocaleString()}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Habits */}
                    <div className="border border-[var(--border)] bg-[var(--surface)]" data-testid="coach-habits-card">
                        <div className="p-5 border-b border-[var(--border)]">
                            <p className="label-tiny mb-1">/ Daily structure</p>
                            <h3 className="font-serif text-2xl">Habits</h3>
                        </div>
                        <form onSubmit={addHabit} className="p-5 border-b border-[var(--border)] grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="add-habit-form">
                            <input required className="input-dark md:col-span-2" placeholder="e.g. 10k steps, 8h sleep…" value={newHabit.name} onChange={(e) => setNewHabit({ ...newHabit, name: e.target.value })} data-testid="habit-name-input" />
                            <select className="input-dark" value={newHabit.frequency} onChange={(e) => setNewHabit({ ...newHabit, frequency: e.target.value })}>
                                <option value="daily">Daily</option>
                                <option value="weekdays">Weekdays</option>
                                <option value="weekly">Weekly</option>
                            </select>
                            <div className="flex gap-2">
                                <input className="input-dark flex-1" placeholder="target" value={newHabit.target} onChange={(e) => setNewHabit({ ...newHabit, target: e.target.value })} />
                                <button type="submit" className="btn-primary inline-flex items-center gap-1" data-testid="save-habit-btn"><Plus size={14} /></button>
                            </div>
                        </form>
                        {habits.length === 0 ? (
                            <p className="p-5 text-sm text-[var(--text-muted)]">No habits yet. Add daily anchors that stack wins.</p>
                        ) : habits.map((h) => (
                            <div key={h.id} className="px-5 py-3 border-b border-[var(--border)] last:border-b-0 flex items-center justify-between">
                                <div>
                                    <p className="font-medium">{h.name}</p>
                                    <p className="text-xs text-[var(--text-muted)] font-mono">{h.frequency} · target: {h.target || "—"}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="font-mono text-xs text-[var(--gold-bright)]">{h.compliance_14d}% · 14d</span>
                                    <button onClick={() => removeHabit(h.id)} className="text-[var(--text-muted)] hover:text-[var(--danger)]"><Trash2 size={13} /></button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Intake (coach view, read-only) */}
                    {intake && (
                        <div className="border border-[var(--gold-deep)] bg-[var(--surface)]" data-testid="coach-intake-card">
                            <div className="p-5 border-b border-[var(--border)]">
                                <p className="label-tiny mb-1">/ From the athlete · intake form</p>
                                <h3 className="font-serif text-2xl">Baseline</h3>
                            </div>
                            <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                {intake.primary_goal && <div><p className="label-tiny mb-1">Goal</p><p className="text-[var(--text)]">{intake.primary_goal}</p></div>}
                                {intake.experience && <div><p className="label-tiny mb-1">Experience</p><p className="text-[var(--text)]">{intake.experience}</p></div>}
                                {intake.equipment && <div><p className="label-tiny mb-1">Equipment</p><p className="text-[var(--text)]">{intake.equipment}</p></div>}
                                {intake.days_per_week && <div><p className="label-tiny mb-1">Days/week</p><p className="text-[var(--text)]">{intake.days_per_week}</p></div>}
                                {(intake.height_ft || intake.height_in) && <div><p className="label-tiny mb-1">Height</p><p className="text-[var(--text)]">{intake.height_ft || 0}′{intake.height_in || 0}″</p></div>}
                                {intake.current_weight && <div><p className="label-tiny mb-1">Current</p><p className="text-[var(--text)]">{intake.current_weight} {intake.weight_unit}</p></div>}
                                {intake.goal_weight && <div><p className="label-tiny mb-1">Goal weight</p><p className="text-[var(--text)]">{intake.goal_weight} {intake.weight_unit}</p></div>}
                            </div>
                            {(intake.injuries || intake.notes) && (
                                <div className="px-5 pb-5 space-y-3">
                                    {intake.injuries && <div><p className="label-tiny mb-1">Injuries / limitations</p><p className="text-sm text-[var(--text)] whitespace-pre-wrap">{intake.injuries}</p></div>}
                                    {intake.notes && <div><p className="label-tiny mb-1">Notes</p><p className="text-sm text-[var(--text)] whitespace-pre-wrap">{intake.notes}</p></div>}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Progress Photos (coach view, read-only) */}
                    {progressPhotos.length > 0 && (
                        <div className="border border-[var(--border)] bg-[var(--surface)]" data-testid="coach-progress-photos-card">
                            <div className="p-5 border-b border-[var(--border)] flex items-center justify-between">
                                <div>
                                    <p className="label-tiny mb-1">/ From the athlete</p>
                                    <h3 className="font-serif text-2xl">Progress photos · {progressPhotos.length}</h3>
                                </div>
                            </div>
                            <div className="p-5 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                                {progressPhotos.map((p) => (
                                    <button
                                        key={p.id}
                                        onClick={() => setPhotoLightbox(p)}
                                        className="aspect-square overflow-hidden bg-[var(--bg-deep)] border border-[var(--border)] hover:border-[var(--gold-deep)]"
                                        data-testid={`coach-photo-${p.id}`}
                                    >
                                        <img
                                            src={`${process.env.REACT_APP_BACKEND_URL || ""}${p.url}`}
                                            alt={new Date(p.created_at).toLocaleDateString()}
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Check-ins */}
                    <div className="border border-[var(--border)] bg-[var(--surface)]" data-testid="coach-checkins-card">                        <div className="p-5 border-b border-[var(--border)]">
                            <p className="label-tiny mb-1">/ Weekly</p>
                            <h3 className="font-serif text-2xl">Check-ins from {client.name.split(" ")[0]}</h3>
                        </div>
                        {checkIns.length === 0 ? (
                            <p className="p-5 text-sm text-[var(--text-muted)]">No check-ins submitted yet. Once they activate the portal, their weekly submissions land here.</p>
                        ) : (
                            <div className="divide-y divide-[var(--border)]">
                                {checkIns.slice(0, 10).map((c) => (
                                    <div key={c.id} className="p-5">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="font-mono text-xs text-[var(--text-muted)]">{new Date(c.created_at).toLocaleString()}</span>
                                            <div className="flex gap-4 text-xs font-mono text-[var(--text-muted)]">
                                                <span>sleep <span className="text-[var(--text)]">{c.sleep_hours}h</span></span>
                                                <span>stress <span className="text-[var(--text)]">{c.stress_1_10}/10</span></span>
                                                <span>mood <span className="text-[var(--text)]">{c.mood_1_10}/10</span></span>
                                                {c.weight != null && <span>weight <span className="text-[var(--text)]">{c.weight}</span></span>}
                                            </div>
                                        </div>
                                        {c.notes && <p className="text-sm text-[var(--text-muted)] whitespace-pre-wrap">{c.notes}</p>}
                                        {c.photo_url && <a href={c.photo_url} target="_blank" rel="noreferrer" className="text-xs text-[var(--gold)] font-mono mt-2 inline-block">view photo →</a>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {payments.length > 0 && (
                        <div className="border border-[var(--border)] bg-[var(--surface)]">
                            <div className="p-5 border-b border-[var(--border)]">
                                <p className="label-tiny mb-1">/ Billing</p>
                                <h3 className="font-serif text-2xl">Payments</h3>
                            </div>
                            {payments.map((p) => (
                                <div key={p.id} className="px-5 py-3 border-b border-[var(--border)] flex justify-between text-sm">
                                    <span className="font-mono text-xs text-[var(--text-muted)]">{new Date(p.created_at).toLocaleDateString()}</span>
                                    <span>${p.amount.toFixed(2)}</span>
                                    <span className={p.status === "COMPLETED" ? "text-[var(--success)]" : "text-[var(--warning)]"}>{p.status}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <ShareDialog
                open={!!shareTarget}
                onClose={() => setShareTarget(null)}
                kind={shareTarget?.kind}
                title={shareTarget?.title}
                details={shareTarget?.details}
                referralLink={user?.referral_code ? `${window.location.origin}/register?ref=${user.referral_code}` : window.location.origin}
            />

            {photoLightbox && (
                <div
                    className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
                    onClick={() => setPhotoLightbox(null)}
                    data-testid="coach-photo-lightbox"
                >
                    <img src={`${process.env.REACT_APP_BACKEND_URL || ""}${photoLightbox.url}`} alt="" className="max-h-full max-w-full object-contain" />
                    <button
                        onClick={(e) => { e.stopPropagation(); setPhotoLightbox(null); }}
                        className="absolute top-4 right-4 text-white/70 hover:text-white text-sm font-mono tracking-[0.2em]"
                    >
                        CLOSE ×
                    </button>
                    <div className="absolute bottom-4 left-4 text-xs text-white/60 font-mono">
                        {new Date(photoLightbox.created_at).toLocaleString()}
                    </div>
                </div>
            )}

            <AiWorkoutGenModal
                open={genOpen}
                onClose={() => setGenOpen(false)}
                clientId={id}
                clientName={client?.name}
                onGenerated={() => { setGenOpen(false); load(); }}
            />
        </div>
    );
}

function AiWorkoutGenModal({ open, onClose, clientId, clientName, onGenerated }) {
    const [form, setForm] = useState({
        goal: "muscle_gain",
        experience: "intermediate",
        training_days: 4,
        start_date: new Date(Date.now() + 24 * 3600 * 1000).toISOString().slice(0, 10),
        equipment: "gym",
        injuries: "",
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [result, setResult] = useState(null);

    if (!open) return null;

    const reset = () => { setForm({ goal: "muscle_gain", experience: "intermediate", training_days: 4, start_date: new Date(Date.now() + 24 * 3600 * 1000).toISOString().slice(0, 10), equipment: "gym", injuries: "" }); setBusy(false); setErr(""); setResult(null); onClose(); };

    const submit = async () => {
        setErr(""); setBusy(true); setResult(null);
        try {
            const { data } = await api.post("/ai/generate-scheduled-workouts", { ...form, training_days: Number(form.training_days), client_id: clientId });
            setResult(data);
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={reset} data-testid="ai-workout-gen-modal">
            <div className="bg-[var(--surface)] border border-[var(--gold-deep)] w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-5 border-b border-[var(--border)]">
                    <div>
                        <p className="label-tiny mb-1 text-[var(--gold-bright)]">/ AI · Claude Sonnet 4.5</p>
                        <h3 className="font-serif text-2xl">Generate workout plan{clientName ? ` for ${clientName}` : ""}</h3>
                    </div>
                    <button onClick={reset} aria-label="Close" data-testid="ai-gen-close"><X size={18} /></button>
                </div>

                <div className="flex-1 overflow-auto p-5 space-y-4">
                    {!result && (
                        <>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="label-tiny block mb-2">Goal</label>
                                    <select className="input-dark" value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} data-testid="gen-goal">
                                        <option value="fat_loss">Fat loss</option>
                                        <option value="muscle_gain">Muscle gain</option>
                                        <option value="recomposition">Recomposition</option>
                                        <option value="strength">Strength</option>
                                        <option value="general">General fitness</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="label-tiny block mb-2">Experience</label>
                                    <select className="input-dark" value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} data-testid="gen-experience">
                                        <option value="beginner">Beginner</option>
                                        <option value="intermediate">Intermediate</option>
                                        <option value="advanced">Advanced</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="label-tiny block mb-2">Total training days</label>
                                    <input type="number" min={1} max={30} className="input-dark" value={form.training_days} onChange={(e) => setForm({ ...form, training_days: e.target.value })} data-testid="gen-days" />
                                </div>
                                <div>
                                    <label className="label-tiny block mb-2">Start date</label>
                                    <input type="date" className="input-dark" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="gen-start" />
                                </div>
                                <div>
                                    <label className="label-tiny block mb-2">Equipment</label>
                                    <select className="input-dark" value={form.equipment} onChange={(e) => setForm({ ...form, equipment: e.target.value })} data-testid="gen-equipment">
                                        <option value="gym">Full gym</option>
                                        <option value="home">Home (DBs + bands)</option>
                                        <option value="minimal">Minimal / bodyweight</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="label-tiny block mb-2">Injuries / limitations <span className="text-[var(--text-faint)] normal-case">(optional)</span></label>
                                <textarea rows={2} className="input-dark" placeholder="e.g. sore left knee, low back tweak, shoulder impingement"
                                    value={form.injuries} onChange={(e) => setForm({ ...form, injuries: e.target.value })} data-testid="gen-injuries" />
                            </div>
                            <p className="text-[10px] font-mono text-[var(--text-muted)]">
                                Workouts will be auto-scheduled on the calendar every other day starting from your start date.
                            </p>
                            {err && <div className="text-sm text-[var(--danger)]" data-testid="gen-err">{err}</div>}
                        </>
                    )}
                    {result && (
                        <div data-testid="gen-result">
                            <div className="flex items-center gap-2 mb-3 text-[var(--gold-bright)]">
                                <CalendarDays size={16} />
                                <p className="font-serif text-2xl">{result.name}</p>
                            </div>
                            {result.description && <p className="text-sm text-[var(--text-muted)] mb-3">{result.description}</p>}
                            <p className="text-xs font-mono text-[var(--gold-bright)] mb-4">
                                {result.workouts_scheduled} workouts scheduled · every other day from {result.start_date}
                            </p>
                            <pre className="text-xs font-mono whitespace-pre-wrap bg-[var(--surface-2)] border border-[var(--border)] p-4 max-h-96 overflow-auto" data-testid="gen-plain-text">
{result.plain_text}
                            </pre>
                        </div>
                    )}
                </div>

                <div className="border-t border-[var(--border)] p-4 flex items-center justify-end gap-2">
                    {!result ? (
                        <>
                            <button onClick={reset} className="btn-ghost" data-testid="gen-cancel">Cancel</button>
                            <button onClick={submit} disabled={busy || !form.training_days || !form.start_date}
                                className="btn-primary inline-flex items-center gap-2" data-testid="gen-submit">
                                {busy ? <><Loader2 size={14} className="animate-spin" /> Designing & scheduling…</> : <><Sparkles size={14} /> Generate & schedule</>}
                            </button>
                        </>
                    ) : (
                        <>
                            <button onClick={() => setResult(null)} className="btn-ghost" data-testid="gen-back">New plan</button>
                            <button onClick={onGenerated} className="btn-primary" data-testid="gen-done">View on calendar</button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
